var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { isMultiValueSubfield } from './multiValueSubfield';
/**
 * The letter subfields that make up the name in each heading tag — the codes the corpus
 * actually carries there (ACTIVE, 26. 10. 2026): PERSONAL `100` uses `a q b c d`, CORPORATE
 * `110` `a b c d g n`, MEETING `111` `a n d c e`.
 */
var NAME_CODES = {
    100: ['a', 'q', 'b', 'c', 'd'],
    110: ['a', 'b', 'c', 'd', 'g', 'n'],
    111: ['a', 'q', 'n', 'd', 'c', 'e', 'g'],
};
/** The subfield a picker must sit on for its pick to be the name. */
var NAME_SUBFIELD = 'a';
/** Already ends a sentence, or an open date (`1950-`): no period before the title. */
var ENDS_BEFORE_TITLE = /[.?!-]$/;
/** The 1XX heading tag a name field copies from (`500` → `100`), or undefined. */
export var headingTagFor = function (fieldTag) {
    var heading = "1".concat(fieldTag.slice(1));
    return fieldTag.length === 3 && NAME_CODES[heading] ? heading : undefined;
};
/** CZNA-1475: the heading a `110` copies from when the target is a jurisdiction. */
var JURISDICTION_HEADING = '151';
/** CZNA-1475: the first indicator of a `110` whose name is a jurisdiction. */
var JURISDICTION_IND1 = '1';
/** The title subfield; the name is what precedes it. */
var TITLE_CODE = 't';
/** Whether `code` is placed before the title in `subfields` (always, when there is none). */
var beforeTitle = function (subfields, code) {
    var title = subfields.findIndex(function (sf) { return sf.code === TITLE_CODE; });
    return title < 0 || subfields.findIndex(function (sf) { return sf.code === code; }) < title;
};
/** A multi-value slot backed by no register or vocabulary: its values are plain text. */
var isFreeTextList = function (sf) { return isMultiValueSubfield(sf)
    && sf.inputType !== 'multi-record-picker' && sf.inputType !== 'multi-vocabulary'; };
/**
 * Slots a name part can be written into, standing before the title (CZNA-1475) — a slot
 * after it is a part of the work: one plain value, or a free-text list (`many`).
 */
var nameSlot = function (subfields, code) {
    if (!beforeTitle(subfields, code))
        return undefined;
    var sf = subfields.find(function (def) { return def.code === code && !def.formOnly && !def.hasComponents; });
    if (!sf)
        return undefined;
    if (!isMultiValueSubfield(sf))
        return { many: false };
    return isFreeTextList(sf) ? { many: true } : undefined;
};
/** The heading row a field copies from: the target's own `1XX`, or a jurisdiction's `151`. */
var headingOf = function (dataFields, headingTag) {
    var first = function (tag) { return dataFields
        .filter(function (f) { return f.fieldTag === tag; })
        .sort(function (x, y) { return x.fieldIndex - y.fieldIndex; })[0]; };
    var own = first(headingTag);
    if (own)
        return { heading: own, codes: NAME_CODES[headingTag], jurisdiction: false };
    var jurisdiction = headingTag === '110' ? first(JURISDICTION_HEADING) : undefined;
    return jurisdiction ? { heading: jurisdiction, codes: [NAME_SUBFIELD], jurisdiction: true } : undefined;
};
/**
 * Whether a pick on `pickerCode` of a `fieldTag` field declaring `subfields` fills the name
 * part by part. False means the picker stores the heading in its own subfield, as before —
 * and asks for nothing more than it did.
 */
export var copiesLinkedHeading = function (fieldTag, pickerCode, subfields) {
    var heading = fieldTag ? headingTagFor(fieldTag) : undefined;
    if (!heading || pickerCode !== NAME_SUBFIELD || !subfields)
        return false;
    return NAME_CODES[heading].some(function (code) { return code !== NAME_SUBFIELD && nameSlot(subfields, code); });
};
/**
 * CZNA-1474 — whether a link on `$a` locks the name of `fieldDef`: a field naming a work
 * (it declares `$t`) whose `$a` pick copies the name part by part.
 */
export var locksLinkedName = function (fieldDef) { return (fieldDef.subfields.some(function (sf) { return sf.code === TITLE_CODE; })
    && copiesLinkedHeading(fieldDef.tag, NAME_SUBFIELD, fieldDef.subfields)); };
/**
 * CZNA-1474 — the name parts beside `$a` that `instance` may not edit: every name code
 * standing before the title while `$a` is linked, none otherwise. A code the template does
 * not declare counts too — it can only be a carried row, and a carried name part left the
 * field with the last pick (`applyLinkedHeadingCopy`), so one there now is part of the name.
 */
export var lockedNameParts = function (fieldDef, instance) {
    var _a;
    if (!((_a = instance === null || instance === void 0 ? void 0 : instance[NAME_SUBFIELD]) === null || _a === void 0 ? void 0 : _a.sourceAuthorityId) || !locksLinkedName(fieldDef))
        return [];
    return NAME_CODES[headingTagFor(fieldDef.tag)]
        .filter(function (code) { return code !== NAME_SUBFIELD && beforeTitle(fieldDef.subfields, code); });
};
/** The `workCodes` of a field declaring `subfields`, set only when there are some. */
var withWorkCodes = function (copy, headingTag, subfields) {
    var workCodes = NAME_CODES[headingTag].filter(function (code) { return !beforeTitle(subfields, code); });
    return workCodes.length ? __assign(__assign({}, copy), { workCodes: workCodes }) : copy;
};
/** Every name slot `subfields` declares for `headingTag`, empty. */
var blankNameSlots = function (headingTag, subfields) {
    var values = {};
    var lists = {};
    NAME_CODES[headingTag].forEach(function (code) {
        var slot = nameSlot(subfields, code);
        if (slot === null || slot === void 0 ? void 0 : slot.many)
            lists[code] = [];
        else if (slot)
            values[code] = '';
    });
    return { values: values, lists: lists };
};
/** `copy` with `lists`, set only when the field declares a list slot. */
var withLists = function (copy, lists) { return (Object.keys(lists).length ? __assign(__assign({}, copy), { lists: lists }) : copy); };
/**
 * The parts of `target`'s heading as `fieldDef` should hold them, or `null` when the field
 * cannot hold the heading part by part (the caller then stores the heading whole).
 */
export var linkedHeadingCopy = function (target, fieldDef) {
    var _a, _b, _c, _d, _e;
    var headingTag = headingTagFor(fieldDef.tag);
    if (!headingTag)
        return null;
    var source = headingOf((_a = target.dataFields) !== null && _a !== void 0 ? _a : [], headingTag);
    if (!source)
        return null;
    var heading = source.heading;
    var nameCodes = source.codes;
    // Same exclusion as the backend's heading copy: a 110 `$c`/`$d`/`$g` the qualifier
    // migration split out of `$a` while `$a` kept the NKP form `Plzeň (Česko)` is already in
    // the name once.
    var a = (_c = (_b = heading.subfields.find(function (sf) { return sf.subfieldCode === NAME_SUBFIELD; })) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '';
    var qualifier = a.includes('(') ? a.slice(a.indexOf('(')) : undefined;
    var rows = heading.subfields.filter(function (sf) {
        var _a;
        return nameCodes.includes(sf.subfieldCode)
            && ((_a = sf.value) !== null && _a !== void 0 ? _a : '') !== ''
            && (sf.subfieldCode === NAME_SUBFIELD || !qualifier || !qualifier.includes(sf.value));
    });
    if (!rows.some(function (sf) { return sf.subfieldCode === NAME_SUBFIELD; }))
        return null;
    var seen = new Set();
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        var slot = nameSlot(fieldDef.subfields, row.subfieldCode);
        if (!slot || (seen.has(row.subfieldCode) && !slot.many))
            return null;
        seen.add(row.subfieldCode);
    }
    var _f = blankNameSlots(headingTag, fieldDef.subfields), values = _f.values, lists = _f.lists;
    rows.forEach(function (row) {
        if (lists[row.subfieldCode])
            lists[row.subfieldCode].push(row.value);
        else
            values[row.subfieldCode] = row.value;
    });
    if (fieldDef.subfields.some(function (sf) { return sf.code === TITLE_CODE; })) {
        // The part that stands before the title is the last one in the field's own order, the
        // order the form writes it in — not the target's row order, which an imported record
        // does not keep (its `subfield_position` is empty). In a list, its last value.
        var last = __spreadArray([], fieldDef.subfields, true).reverse()
            .find(function (sf) { var _a; return values[sf.code] || ((_a = lists[sf.code]) === null || _a === void 0 ? void 0 : _a.length); }).code;
        var list = lists[last];
        var value = (list ? list[list.length - 1] : values[last]).trimEnd();
        var ended = ENDS_BEFORE_TITLE.test(value) ? value : "".concat(value, ".");
        if (list)
            list[list.length - 1] = ended;
        else
            values[last] = ended;
    }
    var ind1 = source.jurisdiction ? JURISDICTION_IND1 : (_d = heading.indicator1) === null || _d === void 0 ? void 0 : _d.trim();
    var offered = (_e = fieldDef.indicator1Def) === null || _e === void 0 ? void 0 : _e.options;
    var takesInd1 = ind1 && (!offered || offered.some(function (o) { return o.value === ind1; }));
    var copy = withLists({ values: values }, lists);
    return withWorkCodes(takesInd1 ? __assign(__assign({}, copy), { ind1: ind1 }) : copy, headingTag, fieldDef.subfields);
};
/**
 * The copy that empties every name part — for a linked name replaced by text or cleared.
 * The parts beside `$a` came from the person the field was linked to, so they leave with
 * that link instead of standing under a name that is no longer theirs.
 */
export var clearedLinkedHeading = function (fieldDef) {
    var headingTag = headingTagFor(fieldDef.tag);
    if (!headingTag)
        return { values: {} };
    var _a = blankNameSlots(headingTag, fieldDef.subfields), values = _a.values, lists = _a.lists;
    return withWorkCodes(withLists({ values: values }, lists), headingTag, fieldDef.subfields);
};
/**
 * Writes `copy` into the field instance at `pathPrefix` (`fields.500.0`), except the
 * `pickerCode` subfield itself — its value AND its FK are the picker's own write, so the two
 * cannot be split across two writers. Returns the value that subfield takes.
 *
 * <p>A name part the instance carries as passthrough (a second `$c` the template has no slot
 * for, CZNA-722) belonged to the old name and goes with it; whatever else is carried there —
 * an AUTHOR_WORK `100 $7` among others — stays.
 */
export var applyLinkedHeadingCopy = function (copy, fieldTag, pathPrefix, pickerCode, getValues, setValue) {
    var _a, _b, _c;
    var options = { shouldDirty: true };
    Object.entries(copy.values).forEach(function (_a) {
        var _b, _c;
        var code = _a[0], value = _a[1];
        if (code === pickerCode)
            return;
        var path = "".concat(pathPrefix, ".").concat(code);
        var current = ((_b = getValues(path)) !== null && _b !== void 0 ? _b : {});
        if (((_c = current.value) !== null && _c !== void 0 ? _c : '') !== value)
            setValue(path, __assign(__assign({}, current), { value: value }), options);
    });
    Object.entries((_a = copy.lists) !== null && _a !== void 0 ? _a : {}).forEach(function (_a) {
        var _b, _c;
        var code = _a[0], list = _a[1];
        var path = "".concat(pathPrefix, ".").concat(code);
        var current = ((_b = getValues(path)) !== null && _b !== void 0 ? _b : {});
        var now = ((_c = current.values) !== null && _c !== void 0 ? _c : []).map(function (entry) { return entry.value; });
        if (JSON.stringify(now) !== JSON.stringify(list)) {
            setValue(path, __assign(__assign({}, current), { value: '', values: list.map(function (value) { return ({ value: value }); }) }), options);
        }
    });
    if (copy.ind1) {
        setValue("".concat(pathPrefix, "._ind1"), { value: copy.ind1 }, options);
    }
    var headingTag = headingTagFor(fieldTag);
    var ticket = (_b = getValues("".concat(pathPrefix, "._ptId"))) === null || _b === void 0 ? void 0 : _b.value;
    if (headingTag && ticket) {
        var path = "passthrough.byInstance.".concat(ticket);
        var carried = getValues(path);
        var nameCodes_1 = NAME_CODES[headingTag].filter(function (code) { var _a; return !((_a = copy.workCodes) === null || _a === void 0 ? void 0 : _a.includes(code)); });
        var kept = carried === null || carried === void 0 ? void 0 : carried.filter(function (row) { var _a; return !nameCodes_1.includes((_a = row.subfield_code) !== null && _a !== void 0 ? _a : ''); });
        if (carried && kept && kept.length !== carried.length)
            setValue(path, kept, options);
    }
    return (_c = copy.values[pickerCode]) !== null && _c !== void 0 ? _c : '';
};
